import { graphql, useLazyLoadQuery } from 'react-relay'
import { EmptyState } from '../../../../components/EmptyState/EmptyState'

import { AdBlockListItem } from '../AdBlockListItem/AdBlockListItem'
import { useGlobalState } from '../../../../state/v2/store'
import { useAdsFilterMenuParams } from '../FilterMenu/useFilterMenuParams'

import {
  ContentType,
  CurrencyType,
  KPI,
  TopPerformingAdsQuery,
  TopPerformingAdsQuery$data,
} from './__generated__/TopPerformingAdsQuery.graphql'
import { useEffect, useState } from 'react'
import { useTrackingContext } from '../../../../analytics/useTracking'
import { trackGalleryViewed } from '../../../../analytics/tracking'
import { useAdSearchQueries } from '../../hooks/useGlobalSearchQueries'
import {
  KpiDisplay,
  metricFilterFieldKey,
} from '../../../../display'
import { FilterBar } from '../FilterBar/FilterBar'
import { removeMetricFromFilter } from '../../../../common'

export type FocusableAd = NonNullable<
  TopPerformingAdsQuery$data['listPerformanceSummarizedMetaAds']
>['data'][number]

const QUERY_TOP_PERFORMING_ADS = graphql`
  query TopPerformingAdsQuery(
    $input: ListPerformanceSummarizedMetaAdsRequest!
    $adPreviewFormat: AdPreviewFormat
    $skip: Boolean!
  ) {
    listPerformanceSummarizedMetaAds(input: $input) @skip(if: $skip) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      metadata {
        currencyType
      }
      data {
        id
        ...AdBlockListItem_PerformanceSummarizedMetaAd
        ...AdDetailsModal_PerformanceSummarizedMetaAd
      }
    }
  }
`

interface TopPerformingAdsProps {
  focusAd: (ad: FocusableAd, currencyType: CurrencyType) => void
  purchasesEnabled: boolean
  leadsEnabled: boolean
}

const PAGE_SIZE = 20
export const TopPerformingAds = ({ focusAd, purchasesEnabled, leadsEnabled }: TopPerformingAdsProps) => {
  const brandId = useGlobalState((b) => b.brand?.map((brand) => brand.id).join(","))
  const [
    {
      topAdMetricsFilters,
      adKpi,
      performanceDateGte,
      performanceDateLte,
      deduplicateAdNames
    },
    setParams,
  ] = useAdsFilterMenuParams()
  const filteredTopAdMetricsFilters = removeMetricFromFilter(adKpi as KPI, topAdMetricsFilters);
  const searchQueries = useAdSearchQueries()
  const [skip, setSkip] = useState(0)
  const res = useLazyLoadQuery<TopPerformingAdsQuery>(
    QUERY_TOP_PERFORMING_ADS,
    {
      skip: !brandId,
      input: {
        filter: {
          brandId: brandId!,
          searchQueries,
          metrics: filteredTopAdMetricsFilters,
          deduplicateAdNames: deduplicateAdNames !== "0",
          filterOutZero: true,
          performanceDateRange:
            performanceDateGte && performanceDateLte
              ? {
                start: performanceDateGte,
                end: performanceDateLte,
              }
              : undefined,
        },
        config: {
          kpi: adKpi as KPI,
        },
        pagination: {
          skip,
          take: PAGE_SIZE,
        },
      },
    },
    { fetchPolicy: 'network-only' }
  )

  const data = res.listPerformanceSummarizedMetaAds?.data || []
  const currencyType = res.listPerformanceSummarizedMetaAds?.metadata.currencyType || "USD"
  const count = res.listPerformanceSummarizedMetaAds?.count || 0
  const { hasNextPage = false, hasPreviousPage = false } =
    res.listPerformanceSummarizedMetaAds?.pageInfo || {}

  const currentPage = skip / PAGE_SIZE + 1
  const totalPages = Math.ceil(count / PAGE_SIZE) || 1

  const ctx = useTrackingContext()
  useEffect(() => {
    if (data.length > 0 && brandId && adKpi) {
      trackGalleryViewed({ brand_id: brandId, account_id: ctx.accountId, kpi: adKpi, evaluation_type: 'top_performing' }, ctx)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length > 0, brandId, adKpi])

  return (
    <div>
      <div className="flex items-center justify-between gap-6">
        <div>
          <h5 className="text-2xl">Top Performing Ads</h5>
          <p className="text-sm">
            KPI: {adKpi ? KpiDisplay[adKpi as KPI] : 'unknown'}
          </p>
        </div>

        <FilterBar.FilterItemWrapper>
          <FilterBar.MetricFilter
            useFilterIcon={false}
            value={filteredTopAdMetricsFilters}
            handleChange={(topAdMetricsFilters) =>
              setParams({ topAdMetricsFilters }, 'pushIn')
            }
            name="metrics"
            renderOptionLabel={(opt) => metricFilterFieldKey(opt, adKpi as KPI)}
            fieldOptions={removeMetricFromFilter(adKpi ? adKpi as KPI : null, [
              'Rate',
              'AmountSpent',
              purchasesEnabled ? 'CostPerPurchase' : 'CostPerWebsitePurchase',
              'Impressions',
              'LandingPageViews',
              'LinkClicks',
              'WebsiteAddToCarts',
              purchasesEnabled ? 'Purchases' : 'WebsitePurchases',
              leadsEnabled ? 'Leads' : 'WebsiteLeads',
            ])}
          />
        </FilterBar.FilterItemWrapper>
      </div>
      <div className="mt-6 overflow-hidden">
        <div className="flex gap-12 overflow-x-scroll">
          {data.map((ad) => (
            <AdBlockListItem
              key={ad.id}
              statBubbleVariant="forestGreen"
              onView={() => focusAd(ad, currencyType)}
              onRefresh={console.log}
              fragmentKey={ad}
              currencyType={currencyType}
            />
          ))}

          {!data.length && (
            <EmptyState message="It looks like we don't have any relevant data for your filters." />
          )}
        </div>
        <div className="mt-4 flex justify-center gap-2">
          {hasPreviousPage && (
            <button
              onClick={() => setSkip(skip - PAGE_SIZE)}
              className="text-xs"
            >
              Prev
            </button>
          )}

          <div>
            Page {currentPage} of {totalPages}
          </div>

          {hasNextPage && (
            <button
              onClick={() => setSkip(skip + PAGE_SIZE)}
              className="text-xs"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  )
}